/**
 * File: Mythbound.App/src/components/UserDisplay.tsx
 * Updated: 2026-01-21
 * Purpose: Shared display for screen names with tag reveal behavior.
 * Author: Mythbound Adventure Studio™ (AI-assisted)
 * Dependencies: Chakra UI Tooltip/HStack/Text, avatar URLs.
 * Pipeline: Identity payload -> UI display.
 * Integration: Campaign rosters, search results, DMs.
 * Security: Avoids exposing full IDs; shows last-4 tags only.
 * Future: Add role badges and accessibility labels.
 */
import React from 'react';
import { Avatar, HStack, Text, Tooltip, TextProps } from '@chakra-ui/react';

export interface UserDisplayProps {
  screenName: string;
  tag: string;
  avatarUrl?: string;
  showTagOnHover?: boolean;
  miniTag?: boolean;
  textProps?: TextProps;
}

export const UserDisplay: React.FC<UserDisplayProps> = ({
  screenName,
  tag,
  avatarUrl,
  showTagOnHover = true,
  miniTag = false,
  textProps
}) => {
  const hasTag = Boolean(tag);
  const tooltipLabel = hasTag ? `${screenName}#${tag}` : screenName;
  const isTooltipDisabled = !showTagOnHover || miniTag || !hasTag;

  return (
    <Tooltip label={tooltipLabel} isDisabled={isTooltipDisabled}>
      <HStack spacing={2}>
        {avatarUrl && <Avatar size="xs" src={avatarUrl} name={screenName} />}
        <Text {...textProps}>
          {screenName}
          {miniTag && hasTag && (
            <Text as="span" fontSize="xs" color="gray.500" ml={1}>
              ({tag})
            </Text>
          )}
        </Text>
      </HStack>
    </Tooltip>
  );
};

export default UserDisplay;
